import { createAsyncThunk } from '@reduxjs/toolkit';

import { ApiPaths } from '../../../api/APIPaths';
import APIDataProvider, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api/ApiDataProvider';
import { ICourse } from '.';

export type TGetCourseAsUser = {
  id: string;
  isSubSectionsAllowed?: boolean;
  deviceTimezone?: string;
};

export type TGetCoursesForUser = {
  limit: number;
  offset: number;
  searchText?: string;
  enrolment?: 'ENROLLED' | 'UNENROLLED';
  deviceTimezone?: string;
  isOneToOne?: boolean;
  type?: 'COURSE' | 'CLASS';
  filter?: string;
  tutorId?: string;
  includeTutorRate?: boolean;
  includeMissedTrialStatus?: boolean;
  includeMissedCancellationStatus?: boolean;
};

export type TUpdateCourse = {
  _id: string;
  title?: string;
  subTitle?: string;
  description?: string;
  curriculum?: string;
  subject?: string;
  grade?: string;
  noOfClasses?: number;
  duration?: number;
  startDate?: string;
  endDate?: string;
  classTime?: string;
  classDays?: string[];
  groupTutorRateCourseType?: string;
};

export type TSendCourseCreditReminderNotification = {
  courseId: string;
};

export type TSendCourseCreditReminderNotificationToAdmin = {
  courseId: string;
  sourceName: string;
};

export type TSendCoursePaymentLink = {
  courseId: string;
  noOfLessons: number;
};

const apiDataProvider = new APIDataProvider();

const getCoursesForUsersModule = async (data: TGetCoursesForUser) => {
  let path = `${ApiPaths.courses}?limit=${data.limit}&offset=${data.offset}`;

  if (data.searchText) {
    path += `&searchText=${encodeURIComponent(data.searchText)}`;
  }

  if (data.enrolment) {
    path += `&enrolment=${data.enrolment}`;
  }

  if (data.deviceTimezone) {
    path += `&deviceTimezone=${data.deviceTimezone}`;
  }

  if (data.isOneToOne !== undefined) {
    path += `&isOneToOne=${data.isOneToOne}`;
  }

  if (data.type) {
    path += `&type=${data.type}`;
  }

  if (data.filter) {
    path += `&filter=${data.filter}`;
  }

  if (data.tutorId) {
    path += `&tutorId=${data.tutorId}`;
  }
  if (data.includeTutorRate) {
    path += '&includeTutorRate=1';
  }
  if (data.includeMissedTrialStatus) {
    path += '&includeMissedTrialStatus=1';
  }
  if (data.includeMissedCancellationStatus) {
    path += '&includeMissedCancellationStatus=1';
  }

  const response = await apiDataProvider.get<
    TApiResponse<TApiPaginationResponse<ICourse>>
  >(path);

  return response.data;
};

export const getEnrolmentCoursesForUsers = createAsyncThunk(
  '/user/courses/enrolment-list',
  getCoursesForUsersModule
);

export const getEnrolmentCoursesForUsersForApp = createAsyncThunk(
  '/user/courses/enrolment-list-for-app',
  getCoursesForUsersModule
);

export const getEnrolmentCoursesForUsersForBundlePayment = createAsyncThunk(
  '/user/courses/enrolment-list-for-bundlePayment',
  getCoursesForUsersModule
);

export const getEnrolmentCoursesForUsersForNotification = createAsyncThunk(
  '/user/courses/enrolment-list-for-notification',
  getCoursesForUsersModule
);

export const loadMoreEnrolmentCoursesForUsers = createAsyncThunk(
  '/user/courses/enrolment-loadMore',
  getCoursesForUsersModule
);

export const getNonEnrolmentCoursesForUsers = createAsyncThunk(
  '/user/courses/non-enrolment-list',
  getCoursesForUsersModule
);

export const loadMoreNonEnrolmentCoursesForUsers = createAsyncThunk(
  '/user/courses/non-enrolment-loadMore',
  getCoursesForUsersModule
);

export const getCourseByIdForUsers = createAsyncThunk(
  '/user/courses/getOne',
  async (data: TGetCourseAsUser) => {
    let path = `${ApiPaths.courses}/${data.id}?subSections=${
      data.isSubSectionsAllowed ? '1' : '0'
    }`;

    if (data.deviceTimezone) {
      path += `&deviceTimezone=${data.deviceTimezone}`;
    }

    const response = await apiDataProvider.get<TApiResponse<ICourse>>(path);

    return response.data;
  }
);

export const updateCourse = createAsyncThunk(
  '/user/courses/updateOne',
  async ({ _id, ...data }: TUpdateCourse) => {
    const path = `${ApiPaths.courses}/${_id}`;
    const response = await apiDataProvider.put<TApiResponse<ICourse>>(
      path,
      data
    );

    return response.data;
  }
);

export const sendCourseCreditReminderNotification = createAsyncThunk(
  '/user/courses/send-credit-reminder-notification',
  async (data: TSendCourseCreditReminderNotification) => {
    const path = `${ApiPaths.courses}/send-credit-reminder-notifications`;
    const response = await apiDataProvider.post<
      TApiResponse<{ status: string }>
    >(path, data);

    return response.data;
  }
);

export const sendCourseCreditReminderNotificationToAdmin = createAsyncThunk(
  '/user/courses/send-credit-reminder-notification-to-admin',
  async (data: TSendCourseCreditReminderNotificationToAdmin) => {
    const path = `${ApiPaths.courses}/send-credit-reminder-notifications-to-admin`;
    const response = await apiDataProvider.post<
      TApiResponse<{ status: string; message: string }>
    >(path, data);

    return response.data;
  }
);

export const sendCoursePaymentLink = createAsyncThunk(
  '/user/courses/send-payment-link',
  async (data: TSendCoursePaymentLink) => {
    const path = `${ApiPaths.courses}/send-payment-link`;
    const response = await apiDataProvider.post<
      TApiResponse<{ status: string; message: string }>
    >(path, data);

    return response.data;
  }
);
